// DesignContentPage4.jsx
import React, { useState, useEffect } from "react";
import {
  TextField,
  Typography,
  Box,
  Grid,
  FormControlLabel,
  Switch,
  MenuItem,
  Button
} from '@mui/material';
import { useStudio } from '../../Context/StudioContext';
import { db } from "../../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { postRsvpApi, postGuestWishApi } from "../../api/cardApi";
import { formatTimeRange, format12Hour } from "..//../utils/FormatTime";
import { useParams } from "react-router-dom";
import ThemeButton from "./ThemeButton";
import { getTranslations } from "../../utils/CardTranslations";


const RSVPForm = ({ data, onClose, source = 'firebase', uniqueCode }) => {


  const [selectedSlot, setSelectedSlot] = useState("");
  const [attendant, setAttendant] = useState("");
  const [guestCount, setGuestCount] = useState("1");
  const [name, setName] = useState("");
  const [guestWish, setGuestWish] = useState("");
  const [attendantError, setAttendantError] = useState("");
  const [nameError, setNameError] = useState("");
  const [wishError, setWishError] = useState("")
  const { cardId } = useParams();

  // -------- Safe defaults for booleans & arrays --------
  const showRSVP = !!data?.showRSVP;
  const showSlotTime = !!data?.showSlotTime;
  const showOnlyWishes = !!data?.showOnlyWishes;
  const slotTimes = data?.slotTimes || [];
  const cardGuestPerPax = Number(data?.guestPerPax || 1);
  const t = getTranslations(data?.language);

  useEffect(() => {
    if (showSlotTime && slotTimes.length > 0 && !selectedSlot) {
      setSelectedSlot(slotTimes[0]);
    }
  }, [showSlotTime, slotTimes, selectedSlot]);

  // -------- Compute guest limit dynamically --------
  const guestLimit = showSlotTime
    ? Number(selectedSlot?.guestPerPax || cardGuestPerPax)
    : cardGuestPerPax;

  const handleSubmit = async () => {

    if (data?.status === 'PENDING PAYMENT') {
      alert("Please complete the payment to enable this feature.");
      return;
    }

    // Validation: must choose attend / not attend
    if (!attendant) {
      setAttendantError(t.rsvpAttendantError);
      return;
    }

    if (!name) {
      setNameError(t.rsvpNameError)
      return;
    }

    if (!guestWish) {
      setWishError(t.rsvpWishError)
      return;
    }

    // if (!cardId) {
    //   alert(t.rsvpCardNotFound);
    //   return;
    // }

    try {

      if (source === 'api') {
        const payload = {
          guestName: name,
          attendanceStatus: attendant === "attend" ? "attending" : "not_attending",
          pax: attendant === "attend" ? Number(guestCount) : 0,
          ...(showSlotTime && selectedSlot && typeof selectedSlot === 'object'
            ? {
              slot: {
                start: selectedSlot.slotTimeStart || "NA",
                end: selectedSlot.slotTimeEnd || "NA",
                label: selectedSlot.label || "NA",
              }
            }
            : {}),
        };
        await postRsvpApi(uniqueCode, payload);

        if (guestWish && guestWish.trim() !== "") {
          const wishPayload = {
            senderName: name || "Tetamu",
            message: guestWish,
          };
          await postGuestWishApi(uniqueCode, wishPayload);
          // Broadcast to the whole app that a wish was successfully posted
          window.dispatchEvent(new Event('wish-posted'));
        }
      } else {
        const rsvpData = {
          name,
          attendant,
          guestCount: attendant === "notAttend" ? 0 : Number(guestCount),
          slot: selectedSlot
            ? {
              start: selectedSlot.slotTimeStart,
              end: selectedSlot.slotTimeEnd,
              label: selectedSlot.label
            }
            : null,
          createdAt: serverTimestamp(),
        };

        // Save to Firestore collection "rsvp"
        await addDoc(collection(db, "cards", cardId, "rsvp"), rsvpData);

        if (guestWish && guestWish.trim() !== "") {
          const wishData = {
            name: name || "Tetamu",
            guestWish,
            createdAt: serverTimestamp(),
          };

          await addDoc(collection(db, "guestWish", cardId, "wishes"), wishData);
        }
      }

      alert(t.rsvpSuccess);
      onClose?.();

    } catch (error) {
      console.error("RSVP Error:", error);
      alert(t.rsvpError);
    }
  };


  const handleAttendantChange = (value) => {
    setAttendant(value);
    setAttendantError("");

    if (value === "notAttend") {
      // Clear and remove values when user is not attending
      setGuestCount("");
      setSelectedSlot(null);
    }
  };


  return (<div style={{ fontFamily: data.fontStyle, textAlign: 'start', width: "300px" }}>

    <Box sx={{ mt: 0, p: 2, border: '1px solid #ddd', borderRadius: '20px', backdropFilter: "blur(3px)", background: "rgba(255, 255, 255, 0.85)" }}>

      <Grid>

        {(showRSVP || showSlotTime) && (
          <>
            <div className="radio-toggle-group" style={{ display: "flex" }}>
              <label
                className={`radio-toggle-attendant ${attendant === 'attend' ? 'attend-selected' : ''}`}
              >
                <input
                  type="radio"
                  name="attendant"
                  value="attend"
                  checked={attendant === 'attend'}
                  onChange={() => handleAttendantChange("attend")}

                />
                {t.rsvpAttend}
              </label>

              <label
                className={`radio-toggle-attendant ${attendant === 'notAttend' ? 'notAttend-selected' : ''}`}
              >
                <input
                  type="radio"
                  name="attendant"
                  value="notAttend"
                  checked={attendant === 'notAttend'}
                  onChange={() => handleAttendantChange("notAttend")}

                />
                {t.rsvpNotAttend}
              </label>
            </div>

            {attendantError && (
              <Typography
                sx={{
                  mt: 1,
                  fontSize: 11,
                  color: "#c62828",
                  textAlign: "center",
                }}
              >
                {attendantError}
              </Typography>
            )}

          </>

        )}


      </Grid>

      <TextField
        label={t.rsvpNameLabel}
        fullWidth
        size="small"
        margin="normal"
        onChange={(e) => {
          const value = e.target.value;
          setName(value);
          if (value.trim() !== "") {
            setNameError("");
          }
        }}
        InputLabelProps={{ shrink: true }}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "30px", // Change this value
          },
          '& .MuiInputBase-input': {

            padding: "10px 14px"
          }
        }}
      />

      {Boolean(nameError) && (
        <Typography
          sx={{
            mt: 1,
            fontSize: 11,
            color: "#c62828",
            textAlign: "center",
          }}
        >
          {nameError}
        </Typography>
      )}

      {(showRSVP || showSlotTime) && (

        < TextField
          select
          label={t.rsvpGuestCountLabel}
          fullWidth
          size="small"
          margin="normal"
          defaultValue="1"
          onChange={(e) => setGuestCount(e.target.value)}
          disabled={attendant === 'notAttend'}
          SelectProps={{
            MenuProps: {
              disableScrollLock: true,
              PaperProps: {
                style: { maxHeight: 200 },
              },
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "30px",
            },
          }}
        >
          {[...Array(guestLimit)].map((_, i) => (
            <MenuItem key={i} value={i + 1} sx={{ fontSize: '13px' }}>
              {i + 1} {t.rsvpGuestUnit}
            </MenuItem>
          ))}
        </TextField>
      )}


      <TextField
        label={t.rsvpWishLabel}
        fullWidth
        multiline
        minRows={2}
        size="small"
        margin="normal"
        onChange={(e) => {
          const value = e.target.value;
          setGuestWish(value)
          if (value.trim() !== "") {
            setWishError("");
          }
        }}
        InputLabelProps={{ shrink: true }}

        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "15px", // Change this value
          },
          '& .MuiInputBase-input': {

            gap: 0
          }
        }}


      />

      {Boolean(wishError) && (
        <Typography
          sx={{
            mt: 1,
            fontSize: 11,
            color: "#c62828",
            textAlign: "center",
          }}
        >
          {wishError}
        </Typography>
      )}


      {showSlotTime && (
        <TextField
          select
          label={t.rsvpSlotLabel}
          fullWidth
          size="small"
          margin="normal"
          disabled={attendant === 'notAttend'}
          value={selectedSlot?.slotTimeStart || ""}
          onChange={(e) => {
            const slot = (data.slotTimes || []).find(
              (s) => s.slotTimeStart === e.target.value
            );
            setSelectedSlot(slot || null);
          }}
          InputLabelProps={{ shrink: true }}
          SelectProps={{
            MenuProps: {
              disableScrollLock: true,
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "30px",
            },
            '& .MuiInputBase-input': {
              fontSize: '12px'
            }
          }}
        >
          {(data.slotTimes || []).map((slot, idx) => (
            <MenuItem key={idx} value={slot.slotTimeStart} sx={{ fontSize: '12px' }}>
              {format12Hour(slot.slotTimeStart)} - {format12Hour(slot.slotTimeEnd)} ({slot.label})
            </MenuItem>
          ))}
        </TextField>
      )}


      <Box sx={{ display: 'grid', gap: 1, mt: 5 }}>
        <ThemeButton themeColor={data?.buttonColour || data?.themeColour} onClick={handleSubmit}>{t.rsvpSubmit}</ThemeButton>

      </Box>

    </Box>
  </div >
  )
};

export default RSVPForm;
